import type { ChatMessages } from "@storytell/cp-client";
import type { CitationNodeProps } from "@storytell/rich-text";
import { type Component, Match, Switch } from "solid-js";
import type { KBSearchResultsCollection, WebSearchResultsCollection } from "~/utils/citationResolver";
import { getCitationType, isKBCitationWithChunk } from "~/utils/citationUtils";
import { ArtifactCitationRenderer } from "./ArtifactCitationRenderer";
import { KBCitationRenderer } from "./KBCitationRenderer";
import { WebCitationRenderer } from "./WebCitationRenderer";

export interface UnifiedCitationRendererProps {
  node: { attrs: { value: string } };
  selected?: boolean;
  webSearchResults?: WebSearchResultsCollection;
  kbSearchResults?: KBSearchResultsCollection;
}

/**
 * Unified citation renderer that detects citation type and delegates to
 * the appropriate renderer:
 * - KBCitationRenderer for KB citations with chunk positions (asset_*_cxp_*)
 * - ArtifactCitationRenderer for basic asset citations (asset_*)
 * - WebCitationRenderer for web citations (web_toolexecution_*)
 */
export const UnifiedCitationRenderer: Component<UnifiedCitationRendererProps> = (props) => {
  const citationType = () => getCitationType(props.node.attrs.value);
  const isKBWithChunk = () => isKBCitationWithChunk(props.node.attrs.value);

  return (
    <Switch fallback={<span class="text-muted-text">[{props.node.attrs.value}]</span>}>
      <Match when={isKBWithChunk()}>
        <KBCitationRenderer node={props.node} selected={props.selected} kbSearchResults={props.kbSearchResults} />
      </Match>
      <Match when={citationType() === "asset"}>
        <ArtifactCitationRenderer {...(props as unknown as CitationNodeProps)} />
      </Match>
      <Match when={citationType() === "web"}>
        <WebCitationRenderer node={props.node} selected={props.selected} webSearchResults={props.webSearchResults} />
      </Match>
    </Switch>
  );
};
